<template>
  <v-layout column class="fullpage">
    <SavingDialog :opened.sync="savingDialogOpened" />

    <WarningDialog
      :opened="erroredReturnsWarningDialogOpened"
      :allow-skip="false"
      warning-title="Some paper file returns need to be corrected"
      warning-text='There are some fields that are invalid or empty. Please press "Close" to review the highlighted the returns below in red.'
      @close="erroredReturnsWarningDialogOpened = false"
    />

    <WarningDialog
      :opened="duplicateReturnWarningDialogOpened"
      :allow-skip="false"
      warning-title="Return names must be unique"
      warning-text="Please make sure that each paper file return has a unique return name."
      @close="duplicateReturnWarningDialogOpened = false"
    />

    <AddPaperFiledReturnsDialog
      :opened.sync="addPaperFiledReturnsDialogOpened"
      :uploading="uploadingReturns"
      @upload-documents="uploadReturns"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status == 'action_required'" type="info">
      <span
        v-if="
          taxReturn.paper_filed_returns &&
            taxReturn.paper_filed_returns.length === 0
        "
      >
        <b>Instruction:</b> You can add <b>paper file returns</b> here or go to
        the next section.
      </span>

      <span v-else>
        <b>Instruction:</b> Please review all paper file returns and fill out
        the details as necessary.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          :is-disabled="saving || pdfLoading"
          icon-right="arrow-right"
          data-test-id="delivery-next-paper-filed-returns"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :loading="saving"
      :disabled="pdfLoading"
      @next="next"
    />

    <v-layout column class="outer-container">
      <v-layout row class="inner-container">
        <!-- Filing Instructions LEFT -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'left'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />

        <!-- Partial: Sidebar -->
        <sc-flex class="sidebar" :is-column="true" :gap="8">
          <v-layout align-center justify-space-between>
            <sc-h5>
              Paper-Filed Returns
            </sc-h5>

            <sc-button
              size="small"
              class="text-none"
              type="primary"
              @click="showAddPaperFiledReturnsDialog"
              :disabled="!editable"
              v-if="editable"
            >
              <v-icon small class="white--text">add</v-icon>

              <span>Add</span>
            </sc-button>
          </v-layout>

          <ul class="page-list">
            <li
              v-for="(paperFiledReturn, index) in taxReturn.paper_filed_returns"
              :key="paperFiledReturn.page"
              :class="{
                selected: currentReturnIndex === index + 1,
                errored:
                  shouldHighlightErroredReturns &&
                  isReturnInvalid(paperFiledReturn),
              }"
            >
              <a @click="currentReturnIndex = index + 1">
                <v-layout align-center>
                  <v-icon small class="mr-2">
                    {{
                      shouldHighlightErroredReturns &&
                      isReturnInvalid(paperFiledReturn)
                        ? 'error'
                        : 'bookmark_border'
                    }}
                  </v-icon>

                  <span>
                    {{ getFullReturnName(paperFiledReturn) }}
                  </span>
                </v-layout>

                <v-layout class="ml-4">
                  <v-chip
                    v-if="paperFiledReturn.is_mailed"
                    label
                    small
                    color="success"
                    style="border-radius: 5px; color: #fff"
                  >
                    Mailed
                  </v-chip>
                </v-layout>
              </a>
            </li>
          </ul>
        </sc-flex>

        <v-layout column class="pdf-container">
          <!-- Partial: Toolbar -->
          <div class="toolbar">
            <v-layout>
              <sc-h4>
                <span v-if="currentReturn">
                  {{ getFullReturnName(currentReturn) }}
                </span>
              </sc-h4>
            </v-layout>

            <div>
              <v-switch
                :input-value="filingInstructionsVisible"
                @change="toggleFilingInstructions"
                class="my-0 py-0"
                hide-details
              >
                <template v-slot:label>
                  <span class="body-2">Show Filing Instructions</span>
                </template>
              </v-switch>
            </div>
          </div>

          <v-layout
            class="page-container"
            justify-center
            align-center
            v-if="!currentReturn"
          >
            <div class="pa-4">
              <span v-if="!editable">
                This page is read-only.
              </span>
              <span v-else>
                Here, you can upload <b>Paper-Filed Returns</b>, or skip this
                step by proceeding to the next section.
              </span>
            </div>
          </v-layout>

          <!-- Partial: Content -->
          <v-layout class="page-container" v-else>
            <v-layout class="pdf-page-container">
              <v-layout
                v-if="!currentPage || pdfLoading"
                align-center
                justify-center
                class="pa-4"
              >
                <v-progress-circular indeterminate color="primary" />
                <span class="ml-2">
                  Loading...
                </span>
              </v-layout>

              <v-layout v-else column style="min-height: 0; flex: auto;">
                <sc-flex
                  class="toolbar__pageNavigation"
                  :is-centered="true"
                  :gap="8"
                >
                  <sc-button-group>
                    <sc-button
                      type="secondary"
                      @click="currentPage -= 1"
                      :is-disabled="currentPage === 1"
                      :is-icon-only="true"
                      icon-left="arrow-left"
                      size="small"
                    ></sc-button>
                  </sc-button-group>
                  <sc-flex :is-y-centered="true" :gap="8">
                    <sc-small>Page:</sc-small>
                    <input
                      :min="1"
                      :max="pdfPageCount"
                      type="number"
                      :value="currentPage"
                      @change="updateCurrentPage($event)"
                    />
                    <sc-small>/ {{ pdfPageCount }}</sc-small>
                  </sc-flex>
                  <sc-button-group>
                    <sc-button
                      type="secondary"
                      @click="currentPage += 1"
                      :is-disabled="currentPage === pdfPageCount"
                      :is-icon-only="true"
                      icon-right="arrow-right"
                      size="small"
                    >
                    </sc-button>
                  </sc-button-group>
                </sc-flex>

                <v-layout column class="scrollable-pdf-page">
                  <div class="pdf-wrapper">
                    <pdf
                      @page-loaded="pdfLoaded = true"
                      @progress="pdfProgress = $event"
                      :page="pdfPageIndex"
                      :src="pdfData"
                      class="pdf ph-no-capture"
                    />
                  </div>
                </v-layout>
              </v-layout>
            </v-layout>

            <sc-flex
              :is-column="true"
              :gap="16"
              v-if="currentReturn"
              class="sidebar--right"
            >
              <v-form ref="form">
                <sc-h3>Return Details</sc-h3>

                <sc-flex :is-column="true" :gap="8">
                  <sc-flex :is-column="true" :gap="4" class="form-control">
                    <span
                      >Return name:
                      <span class="error--text">*</span>
                    </span>

                    <v-combobox
                      solo
                      hide-details
                      clearable
                      class="select-input"
                      :items="customReturnNames"
                      item-text="name"
                      item-value="name"
                      :value="currentReturn.custom_return_name"
                      :append-icon="null"
                      :disabled="!editable"
                      :rules="[rules.required]"
                      @change="addCustomPaperFiledReturnName($event)"
                    >
                      <v-layout
                        slot="item"
                        slot-scope="props"
                        align-center
                        justify-space-between
                      >
                        <span>
                          {{ props.item.name }}
                        </span>

                        <v-btn
                          icon
                          ripple
                          @click.prevent.stop="
                            deleteCustomPaperFiledReturnName(props.item)
                          "
                        >
                          <v-icon
                            color="error lighten-1"
                            class="material-icons-outlined"
                            >delete</v-icon
                          >
                        </v-btn>
                      </v-layout>
                    </v-combobox>
                  </sc-flex>

                  <sc-flex :is-column="true" :gap="4" class="form-control">
                    <span
                      >Instructions:
                      <span class="error--text">*</span>
                    </span>

                    <v-textarea
                      solo
                      hide-details
                      placeholder="Enter instructions"
                      v-model="currentReturn.instructions"
                      class="mt-0 mb-0 select-input"
                      :disabled="!editable"
                      :rules="[rules.required]"
                    />
                  </sc-flex>

                  <sc-flex :is-column="true" :gap="4" class="form-control">
                    <span
                      >Mailing address:
                      <span class="error--text">*</span>
                    </span>

                    <v-textarea
                      solo
                      hide-details
                      placeholder="Enter mailing address"
                      v-model="currentReturn.mailing_address"
                      class="mt-0 mb-0 select-input"
                      :disabled="!editable"
                      :rules="[rules.required]"
                    />
                  </sc-flex>

                  <v-divider class="mt-4" />

                  <v-layout>
                    <v-btn
                      color="error"
                      class="text-none"
                      block
                      flat
                      @click="removeReturn"
                      :disabled="!editable"
                    >
                      <v-icon left class="material-icons-outlined"
                        >delete</v-icon
                      >
                      Remove this return
                    </v-btn>
                  </v-layout>
                </sc-flex>
              </v-form>
            </sc-flex>
          </v-layout>
        </v-layout>

        <!-- Filing Instructions RIGHT -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'right'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />
      </v-layout>

      <!-- Filing Instructions BOTTOM -->
      <FilingInstructionsPanel
        v-if="filingInstructionsPosition === 'bottom'"
        ref="filingInstructionsPanel"
        :taxReturn="taxReturn"
        :position="filingInstructionsPosition"
        :visible="filingInstructionsVisible"
        :highlighted-block-numbers="highlightedBlockNumbers"
        @update:highlighted-block-numbers="
          $emit('update:highlightedBlockNumbers', $event)
        "
        @update:position="$emit('update:filingInstructionsPosition', $event)"
        @update:visible="$emit('update:filingInstructionsVisible', $event)"
      />
    </v-layout>
  </v-layout>
</template>

<script>
import pdf from 'vue-pdf';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import WarningDialog from '@/components/returns/WarningDialog';
import SavingDialog from '@/components/returns/SavingDialog';
import AddPaperFiledReturnsDialog from '@/components/returns/AddPaperFiledReturnsDialog';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import FilingInstructionsPanel from '@/components/returns/FilingInstructionsPanel';
import FilingInstructionsPanelMixin from '@/mixins/FilingInstructionsPanelMixin';

export default {
  mixins: [FirmPreferenceMixins, FilingInstructionsPanelMixin],

  components: {
    pdf,
    ReadonlyAlert,
    WarningDialog,
    SavingDialog,
    AddPaperFiledReturnsDialog,
    FilingInstructionsPanel,
  },

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },

    filingInstructionsPosition: {
      type: String,
    },

    filingInstructionsVisible: {
      type: Boolean,
      default: false,
    },

    highlightedBlockNumbers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentReturnIndex: null,

      pdfProgress: 0,
      pdfLoaded: false,
      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfPageCount: null,

      currentPage: null,

      saving: false,
      savingDialogOpened: false,

      // States for addPaperFiledReturnsDialog
      addPaperFiledReturnsDialogOpened: false,
      uploadingReturns: false,

      // States for forms with errors
      erroredReturnsWarningDialogOpened: false,
      shouldHighlightErroredReturns: false,
      duplicateReturnWarningDialogOpened: false,

      customReturnNames: [],

      rules: {
        required: value => !!value || 'Required.',
      },
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    currentReturn: {
      get() {
        if (this.currentReturnIndex === null) return null;
        return this.taxReturn.paper_filed_returns[this.currentReturnIndex - 1];
      },
      set(value) {
        if (this.currentReturnIndex !== null) {
          this.$set(
            this.taxReturn.paper_filed_returns,
            this.currentReturnIndex - 1,
            value,
          );
        }
      },
    },

    isCurrentReturnFromOriginalPackage() {
      if (!this.currentReturn) {
        return false;
      }

      return this.currentReturn.pages && this.currentReturn.pages.length > 0;
    },

    editable() {
      return this.taxReturn.status === 'action_required';
    },

    pdfPageIndex() {
      if (this.currentReturn) {
        if (this.currentReturn.pages) {
          return this.currentReturn.pages[this.currentPage - 1];
        } else {
          return this.currentPage;
        }
      }

      return null;
    },
  },

  methods: {
    isEmpty,

    async showSavingDialog() {
      if (this.savingDialogOpened) {
        return;
      }

      try {
        this.savingDialogOpened = true;
        const taxReturn = await this.save(false);

        if (taxReturn) {
          this.$emit('paper-filed-returns-updated', taxReturn);
        }
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      await this.showSavingDialog();
      return true;
    },

    async save() {
      if (!this.editable) {
        return;
      }

      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        paper_filed_returns: this.taxReturn.paper_filed_returns,
      });

      return response.data;
    },

    async finishReviewing() {
      if (this.hasErrors()) {
        this.erroredReturnsWarningDialogOpened = true;
        this.shouldHighlightErroredReturns = true;
        this.validateInputs();
        return;
      } else if (this.hasDuplicateReturnNames()) {
        this.duplicateReturnWarningDialogOpened = true;
        return;
      }

      const taxReturn = await this.save();

      this.$emit('paper-filed-returns-updated', taxReturn);
    },

    getFullReturnName(paperFiledReturn) {
      return paperFiledReturn.custom_return_name;
    },

    next() {
      this.$emit('next');
    },

    hasErrors() {
      if (!this.taxReturn.paper_filed_returns) {
        return false;
      }

      return this.taxReturn.paper_filed_returns.some(paperFiledReturn => {
        return this.isReturnInvalid(paperFiledReturn);
      });
    },

    hasDuplicateReturnNames() {
      if (!this.taxReturn.paper_filed_returns) {
        return false;
      }

      const returnNames = this.taxReturn.paper_filed_returns.map(
        paperFiledReturn => paperFiledReturn.custom_return_name,
      );

      return new Set(returnNames).size !== returnNames.length;
    },

    isReturnInvalid(paperFiledReturn) {
      if (isEmpty(paperFiledReturn.instructions)) {
        return true;
      } else if (isEmpty(paperFiledReturn.mailing_address)) {
        return true;
      } else {
        return false;
      }
    },

    validateInputs() {
      this.$refs.form.validate();
    },

    async getCustomPaperFiledReturnNames() {
      const response = await API.firm.getCustomPaperFiledReturnNames({
        firmId: this.currentUserFirm.id,
      });

      this.customReturnNames = response.data;
    },

    async addCustomPaperFiledReturnName(item) {
      // Depends on whether item is selected or typed, it can be either an object or just a string;
      const name = item && item.name ? item.name : item;

      this.currentReturn.custom_return_name = name;

      if (isEmpty(name)) {
        return;
      }

      const response = await API.firm.addCustomPaperFiledReturnName({
        firmId: this.currentUserFirm.id,
        name,
      });

      this.customReturnNames.push(response.data);
    },

    async deleteCustomPaperFiledReturnName(item) {
      const response = await API.firm.deleteCustomPaperFiledReturnName({
        firmId: this.currentUserFirm.id,
        id: item.id,
      });

      this.customReturnNames = this.customReturnNames.filter(
        customReturnName => customReturnName.id !== item.id,
      );
    },

    showAddPaperFiledReturnsDialog() {
      this.addPaperFiledReturnsDialogOpened = true;
    },

    async uploadReturns(paperFiledReturns) {
      try {
        this.uploadingReturns = true;

        for (const paperFiledReturn of paperFiledReturns) {
          const {
            data: { id, upload_link },
          } = await API.taxReturn.getUploadLinkForPaperFiledReturn({
            companyId: this.currentCompany.id,
          });

          await API.integration.uploadToDropbox(upload_link, paperFiledReturn);

          const response = await API.taxReturn.processPaperFiledReturn({
            fileId: id,
            taxReturnId: this.taxReturn.id,
            documentName: paperFiledReturn.name,
            companyId: this.currentCompany.id,
          });

          await this.addReturn(
            response.data.document_name.replace('.pdf', ''),
            response.data.document_location,
          );
          this.addPaperFiledReturnsDialogOpened = false;
        }
      } catch (error) {
        console.log(error);

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the documents.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.uploadingReturns = false;
      }
    },

    async addReturn(returnName, documentPath) {
      const newReturn = {
        custom_return_name: returnName,
        document_path: documentPath,
        instructions:
          'Please download the return, print it, sign using a black pen, and mail all pages in the file to the provided address.',
        mailing_address: '',
      };

      if (!Array.isArray(this.taxReturn.paper_filed_returns)) {
        this.taxReturn.paper_filed_returns = [];
      }

      this.taxReturn.paper_filed_returns.push(newReturn);

      this.currentReturnIndex = this.taxReturn.paper_filed_returns.length;
    },

    async removeReturn() {
      this.taxReturn.paper_filed_returns.splice(this.currentReturnIndex - 1, 1);
      this.currentReturnIndex = this.currentReturnIndex - 1;
    },

    async loadReturnPdf() {
      try {
        this.pdfLoading = true;

        if (this.currentReturn.pages) {
          const response = await API.integration.downloadDocument({
            companyId: this.currentCompany.id,
            link: this.taxReturn.original_file,
          });

          const pdfSrc = response.data.document.data.link;
          const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

          this.pdfDoc = await PDFDocument.load(pdfBytes);
          this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });

          this.pdfPageCount = this.currentReturn.pages.length;
          this.currentPage = 1;
        } else {
          const response = await API.integration.downloadDocument({
            companyId: this.currentCompany.id,
            link: this.currentReturn.document_path,
          });

          const pdfSrc = response.data.document.data.link;
          const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

          this.pdfDoc = await PDFDocument.load(pdfBytes);
          this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });

          this.pdfPageCount = this.pdfDoc.getPageCount();
          this.currentPage = 1;
        }
      } finally {
        this.pdfLoading = false;
      }
    },

    updateCurrentPage(e) {
      this.currentPage = parseInt(e.target.value);
    },
  },

  watch: {
    async currentReturnIndex() {
      if (this.currentReturn) {
        await this.loadReturnPdf();
      }
    },
  },

  async beforeDestroy() {
    await this.beforeClose();
  },

  async mounted() {
    this.getCustomPaperFiledReturnNames();

    if (
      this.taxReturn.paper_filed_returns &&
      this.taxReturn.paper_filed_returns.length > 0
    ) {
      this.currentReturnIndex = 1;
    }
  },
};
</script>

<style lang="postcss" scoped>
.fullpage {
  height: calc(100dvh - 60px); /* 60px is the height of toolbar */
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  width: 100%;
  max-width: 260px;
  overflow: auto;
  padding: 20px 16px 32px;

  background-color: #fff;
  border-right: 1px solid var(--gray-300);
}

@media (max-width: 860px) {
  .sidebar {
  }
}

.pdf-container {
  flex: auto;
  overflow-y: auto;
  position: relative;
  min-height: 0;
}

.toolbar {
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  min-height: 40px;
  justify-content: space-between;
  padding: 0 16px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-300);
}

.toolbar__pageNavigation {
  background: #fff;
  border-bottom: 1px solid var(--gray-200);
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: calc(100% - 360px);

  & >>> sc-button[is-disabled] {
    pointer-events: none;
  }
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  min-height: 0;
  width: 100%;
}

.pdf-page-container {
  position: relative;
  flex-direction: column;
  overflow: hidden;
}

.scrollable-pdf-page {
  overflow: auto;
  min-height: 0;
  flex: auto;
}

.pdf-wrapper {
  position: relative;
  display: inline-block;
  max-width: 1000px;
}

.pdf {
  position: relative;
  padding: 0;
  width: 100%;
}

.page-list {
  padding-left: 0px;
}

.page-list li {
  padding: 4px;
  list-style: none;
}

.page-list li a {
  color: #444;
  text-decoration: none;
}

.page-list li a i {
  color: #444;
}

.page-list li.errored a,
.page-list li.errored a i,
.page-list li.unreviewed a,
.page-list li.unreviewed a i {
  color: var(--red-700);
}

.page-list li.selected {
  background-color: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
}

.sidebar--right {
  box-sizing: border-box;
  overflow-y: auto;
  max-width: 320px;
  width: 100%;
  padding: 16px;
  padding-bottom: 100px;

  background-color: #fff;
  border-left: 1px solid var(--gray-200);
}

.sidebar--right * {
  box-sizing: border-box !important;
}

.form-control {
  margin-top: 8px;

  & >>> .v-input__control {
    min-height: 30px;
    font-size: 15px;
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd;
    border-radius: 8px;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500);
  }
}

.form-control {
  & >>> .readonly-field .v-input__slot {
    background-color: #f1f1f1;
    box-shadow: none !important;
    color: #000;
  }

  & >>> .readonly-field .v-input__slot input {
    cursor: not-allowed;
  }
}

.tax-detail-card {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #e0e0e0;
}

.card-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.title-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  height: 20px;
  width: 20px;
  margin: 0;
}

.outer-container {
  min-height: 0;
}

.inner-container {
  min-height: 0;
}
</style>
